import {getScrollTop} from '@theme/scroll-container';

class SopperHeader extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const {signal} = this.abort;
    this.group = this.closest('#header-group');
    this.lastY = getScrollTop();
    this.desktopScrollAnchor = this.lastY;
    this.addEventListener('click', event => {
      const trigger = event.target.closest('button');
      if (trigger?.matches('[data-menu-toggle]')) this.open('sopper-mobile-menu', trigger);
      if (trigger?.matches('[data-menu-close]')) document.getElementById('sopper-mobile-menu')?.closeDialog();
      if (trigger?.matches('[data-search-toggle]')) this.open('search-modal', trigger);
      if (trigger?.matches('[data-cart-toggle]')) this.open('cart-drawer', trigger);
    }, {signal});
    this.querySelectorAll('details').forEach(details => {
      details.addEventListener('toggle', () => {
        details.querySelector('summary').setAttribute('aria-expanded', String(details.open));
        if (details.open) this.group?.classList.remove('sopper-header-hidden');
      }, {signal});
    });
    document.addEventListener('click', event => {
      this.querySelectorAll('details[open]').forEach(d => { if (!d.contains(event.target)) d.open = false; });
    }, {signal});
    this.addEventListener('keydown', event => {
      const details = event.target.closest('details[open]');
      if (event.key === 'Escape' && details) {
        event.preventDefault(); event.stopPropagation(); details.open = false; details.querySelector('summary').focus();
      }
      if (details && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
        const controls = [...details.querySelectorAll('summary, a, button')];
        let i = controls.indexOf(document.activeElement) + (event.key === 'ArrowDown' ? 1 : -1);
        event.preventDefault(); controls[(i + controls.length) % controls.length]?.focus();
      }
    }, {signal});
    const onScroll = () => {
      const y = getScrollTop();
      const compact = matchMedia('(max-width:1099px)').matches || this.hasAttribute('data-compact');
      const activeOverlay = document.querySelector('dialog[open], [popover]:popover-open') || [...this.querySelectorAll('details[open]')].some(details => details.getClientRects().length > 0);
      if (compact) {
        if (y < 16 || activeOverlay) this.group?.classList.remove('sopper-header-hidden');
        else if (Math.abs(y - this.lastY) > 5) this.group?.classList.toggle('sopper-header-hidden', y > this.lastY && y > this.offsetHeight);
        this.desktopScrollAnchor = y;
      } else if (y < 16 || activeOverlay || this.querySelector(':focus-visible')) {
        this.group?.classList.remove('sopper-header-hidden');
        this.desktopScrollAnchor = y;
      } else if (Math.abs(y - this.desktopScrollAnchor) > 6) {
        this.group?.classList.toggle('sopper-header-hidden', y > this.desktopScrollAnchor && y > this.offsetHeight);
        this.desktopScrollAnchor = y;
      }
      this.lastY = y;
    };
    document.addEventListener('scroll', onScroll, {capture: true, passive: true, signal});
    this.addEventListener('focusin', () => {
      if (innerWidth >= 1100 && !this.hasAttribute('data-compact')) this.group?.classList.remove('sopper-header-hidden');
    }, {signal});
    this.observer = new MutationObserver(() => {
      for (const [selector, id] of [['[data-menu-toggle]', 'sopper-mobile-menu'], ['[data-search-toggle]', 'search-modal'], ['[data-cart-toggle]', 'cart-drawer']]) {
        const open = Boolean(document.getElementById(id)?.querySelector('dialog[open]'));
        this.querySelector(selector)?.setAttribute('aria-expanded', String(open));
        if (open) this.group?.classList.remove('sopper-header-hidden');
      }
    });
    this.observer.observe(document.body, {subtree: true, attributes: true, attributeFilter: ['open']});
    this.measure = () => {
      this.removeAttribute('data-compact');
      if (innerWidth >= 1100) {
        const nav = this.querySelector('.sopper-nav');
        const list = nav.querySelector('ul');
        if (list.scrollWidth > nav.clientWidth - 64) this.setAttribute('data-compact', '');
      }
      if (innerWidth >= 1100 && !this.hasAttribute('data-compact')) document.getElementById('sopper-mobile-menu')?.closeDialog();
      document.body.style.setProperty('--header-height', `${this.offsetHeight}px`);
      document.body.style.setProperty('--header-group-height', `${this.group?.offsetHeight || this.offsetHeight}px`);
      document.documentElement.style.setProperty('--sopper-visual-height', `${window.visualViewport?.height || innerHeight}px`);
      document.documentElement.style.setProperty('--sopper-visual-top', `${window.visualViewport?.offsetTop || 0}px`);
      onScroll();
    };
    window.addEventListener('resize', this.measure, {signal});
    window.visualViewport?.addEventListener('resize', this.measure, {signal});
    this.resizeObserver = new ResizeObserver(() => {
      document.body.style.setProperty('--header-height', `${this.offsetHeight}px`);
      document.body.style.setProperty('--header-group-height', `${this.group?.offsetHeight || this.offsetHeight}px`);
    });
    this.resizeObserver.observe(this);
    this.measure();
    document.fonts.ready.then(() => {if (this.isConnected) this.measure();});
  }
  async open(id, trigger) {
    const component = document.getElementById(id);
    if (!component) return;
    await customElements.whenDefined(component.localName);
    this.group?.classList.remove('sopper-header-hidden');
    const dialog = component.querySelector('dialog');
    dialog?.addEventListener('close', () => {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus({preventScroll: true});
    }, {once: true});
    if (id === 'cart-drawer') component.open();
    else component.showDialog();
    trigger.setAttribute('aria-expanded', 'true');
  }
  disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); this.resizeObserver?.disconnect(); }
}
if (!customElements.get('sopper-header')) customElements.define('sopper-header', SopperHeader);
